import { useSetAtom } from 'jotai';
import { cn } from '@localagent/ui/lib/utils';
import { Popover } from '@localagent/ui/components/popover';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { chatFilesOpenAtom, chatDockOpenAtom } from '../../stores/layout/atoms';
import { useChatPanelLayout, type ChatPanelPosition } from '../../hooks/useChatPanelLayout';
import { EdgePositionPicker, type EdgePositionOption } from './EdgePositionPicker';

function ctrlClass(active: boolean, disabled = false): string {
  return cn(
    'rounded-md p-1.5 transition-colors',
    disabled
      ? 'cursor-not-allowed text-muted-foreground/40'
      : active
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-accent',
  );
}

const FILE_PANEL_OPTIONS: EdgePositionOption<ChatPanelPosition>[] = [
  { value: 'left', title: 'File panel left', rect: [3, 3, 8, 18] },
  { value: 'right', title: 'File panel right', rect: [13, 3, 8, 18] },
  { value: 'top', title: 'File panel top', rect: [3, 3, 18, 8] },
  { value: 'bottom', title: 'File panel bottom', rect: [3, 13, 18, 8] },
];

function FolderIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
  );
}

/** Hide + position. Only mounted on the Files tree, so a workspace is attached. */
export function ChatLayoutControls() {
  const setFilesOpen = useSetAtom(chatFilesOpenAtom);
  const { position, setPosition } = useChatPanelLayout();

  return (
    <div className="flex items-center gap-1" data-testid="chat-layout-controls">
      <Tooltip label="Hide files">
      <button
        type="button"
        data-testid="file-panel-hide"
        onClick={() => setFilesOpen(false)}
        className={ctrlClass(true)}
        title="Hide files"
        aria-label="Hide files"
        aria-pressed
      >
        <FolderIcon />
      </button>
      </Tooltip>
      <Tooltip label="File panel position">
      <Popover
        align="start"
        aria-label="File panel position"
        triggerClassName="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent"
        trigger={
          <span data-testid="chat-layout-more" className="flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
            </svg>
          </span>
        }
      >
        <div className="flex flex-col gap-1.5">
          <span className="px-1 text-caption font-medium text-muted-foreground">File panel position</span>
          <EdgePositionPicker
            options={FILE_PANEL_OPTIONS}
            active={position}
            onChange={setPosition}
            testid="file-panel-position-picker"
            optionTestidPrefix="file-panel-position"
          />
        </div>
      </Popover>
      </Tooltip>
    </div>
  );
}

export function FilesToggleButton({ hasWorkspace, open }: { hasWorkspace: boolean; open: boolean }) {
  const setFilesOpen = useSetAtom(chatFilesOpenAtom);
  const setDockOpen = useSetAtom(chatDockOpenAtom);
  const label = !hasWorkspace ? 'Files (attach a workspace)' : open ? 'Hide files' : 'Show files';

  return (
    <Tooltip label={label}>
      <button
        type="button"
        data-testid="toggle-file-browser"
        onClick={() => {
          setFilesOpen(!open);
          if (!open) setDockOpen(false);
        }}
        disabled={!hasWorkspace}
        className={ctrlClass(open, !hasWorkspace)}
        title={label}
        aria-label={open ? 'Hide files' : 'Show files'}
        aria-pressed={open}
      >
        <FolderIcon />
      </button>
    </Tooltip>
  );
}
